import React, { useState, useEffect } from 'react';
import { getYearlyStats, getBookings } from '../services/api';
import { BarChart3Icon, TrendingUpIcon, PackageIcon, TagIcon, CalendarIcon, ClockIcon, ListIcon } from 'lucide-react';
import { Booking } from '../types';

interface YearlyStats {
  year: number;
  total_sales: number;
  rentals_by_product: { [key: string]: number };
  promo_codes_used: { [key: string]: number };
  rentals_by_month: { [key: string]: number };
  next_day_pickup_count: number;
  total_bookings: number;
}

const BookingStats: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [stats, setStats] = useState<YearlyStats | null>(null);
  const [yearBookings, setYearBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    fetchStats();
  }, [selectedYear]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsData, bookingsResponse] = await Promise.all([
        getYearlyStats(selectedYear),
        getBookings()
      ]);
      setStats(statsData);
      
      // Filter bookings by selected year and confirmed/completed status
      const allBookings = bookingsResponse.items || [];
      const filteredBookings = allBookings.filter((booking: Booking) => {
        if (!booking.start_time_utc) return false;
        const bookingDate = new Date(booking.start_time_utc);
        const status = String(booking.status || '').toUpperCase();
        return bookingDate.getFullYear() === selectedYear && 
               (status === 'CONFIRMED' || status === 'COMPLETED');
      });
      
      // Sort by start date
      filteredBookings.sort((a: Booking, b: Booking) => {
        const dateA = new Date(a.start_time_utc!).getTime();
        const dateB = new Date(b.start_time_utc!).getTime();
        return dateB - dateA; // Newest first
      });
      
      setYearBookings(filteredBookings);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const monthOrder = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const sortedMonthlyRentals = monthOrder
    .map(month => ({
      month,
      count: stats?.rentals_by_month[month] || 0
    }))
    .filter(item => item.count > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        {error}
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Year Selector */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Booking Statistics</h2>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Total Sales */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Sales</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                ${stats.total_sales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <TrendingUpIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Total Bookings */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Bookings</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total_bookings}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <CalendarIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Next Day Pickups */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Next Day Pickups</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{stats.next_day_pickup_count}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <ClockIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Rentals by Product */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <PackageIcon className="h-5 w-5 text-gray-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Rentals by Product Type</h3>
        </div>
        {Object.keys(stats.rentals_by_product).length > 0 ? (
          <div className="space-y-3">
            {Object.entries(stats.rentals_by_product)
              .sort(([, a], [, b]) => b - a)
              .map(([product, count]) => (
                <div key={product} className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <span className="text-sm font-medium text-gray-900">{product}</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-48 bg-gray-200 rounded-full h-2 mr-3">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{
                          width: `${(count / Math.max(...Object.values(stats.rentals_by_product))) * 100}%`
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 w-8 text-right">{count}</span>
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No rental data available</p>
        )}
      </div>

      {/* Promo Codes Used */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <TagIcon className="h-5 w-5 text-gray-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Promo Codes Used</h3>
        </div>
        {Object.keys(stats.promo_codes_used).length > 0 ? (
          <div className="space-y-2">
            {Object.entries(stats.promo_codes_used)
              .sort(([, a], [, b]) => b - a)
              .map(([code, count]) => (
                <div key={code} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-900">{code}</span>
                  <span className="text-sm font-semibold text-blue-600">{count} uses</span>
                </div>
              ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No promo codes used this year</p>
        )}
      </div>

      {/* Rentals by Month */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <BarChart3Icon className="h-5 w-5 text-gray-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Rentals by Month</h3>
        </div>
        {sortedMonthlyRentals.length > 0 ? (
          <div className="space-y-3">
            {sortedMonthlyRentals.map(({ month, count }) => (
              <div key={month} className="flex items-center justify-between">
                <div className="flex items-center flex-1">
                  <span className="text-sm font-medium text-gray-900 w-24">{month}</span>
                </div>
                <div className="flex items-center flex-1">
                  <div className="flex-1 bg-gray-200 rounded-full h-2 mr-3">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{
                        width: `${(count / Math.max(...sortedMonthlyRentals.map(m => m.count))) * 100}%`
                      }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 w-8 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No rental data available</p>
        )}
      </div>

      {/* Bookings List */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <ListIcon className="h-5 w-5 text-gray-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Bookings Included in Statistics ({yearBookings.length})</h3>
        </div>
        {yearBookings.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {yearBookings.map((booking) => {
                  const startDate = booking.start_time_utc ? new Date(booking.start_time_utc) : null;
                  const totalPrice = booking.total_price ? parseFloat(String(booking.total_price)) / 100 : 0;
                  const customerName = booking.customer_name || booking.customer?.full_name || 'Unknown';
                  
                  return (
                    <tr key={booking.booking_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {startDate ? startDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {customerName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          booking.status === 'CONFIRMED' || booking.status === 'confirmed'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {booking.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                        ${totalPrice.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={3} className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                    Total:
                  </td>
                  <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                    ${yearBookings.reduce((sum, booking) => {
                      const price = booking.total_price ? parseFloat(String(booking.total_price)) / 100 : 0;
                      return sum + price;
                    }, 0).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No bookings found for {selectedYear}</p>
        )}
      </div>
    </div>
  );
};

export default BookingStats;
